import { AddressComponentService } from './../services/address.component.service';
import { Component, OnInit, Input, Output, EventEmitter, AfterContentChecked } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { FullAddress } from '../models/address.models';
import { TogetherBaseAddress } from './../models/api.models';
import * as _ from 'lodash-es';

@Component({
    selector: 'app-address-editor',
    templateUrl: './address.editor.component.html',
    standalone: false
})
export class AddressEditorComponent implements OnInit {
    /**
     * Quick Address data
     *
     * @type {FullAddress}
     * @memberof AddressEditorComponent
     */
    @Input() qasAddress: FullAddress;
    /**
     * Incomming address from parent
     *
     * @type {TogetherBaseAddress}
     * @memberof AddressEditorComponent
     */
    @Input() incomingAddress: TogetherBaseAddress;

    /**
    * Incomming address from parent
    *
    * @type {number}
    * @memberof AddressEditorComponent
    */
    @Input() index: number = 0;
    /**
     * Outgoing address emitted to the parent
     *
     * @type {EventEmitter<TogetherBaseAddress>}
     * @memberof AddressEditorComponent
     */
    @Output() addressIsValid: EventEmitter<object> = new EventEmitter<object>();

    /**
     * Outputs form to parent address component so this can be outputted to other components.
     *
     * @type {EventEmitter<FormGroup>}
     * @memberof AddressEditorComponent
     */
    @Output() form_out: EventEmitter<UntypedFormGroup> = new EventEmitter<UntypedFormGroup>(); // HERE

    /**
     * Form for editing the address
     *
     * @memberof AddressEditorComponent
     */
    formAddressEditor: UntypedFormGroup;

    /**
     * House name
     *
     * @type {FormControl}
     * @memberof AddressEditorComponent
     */
    addressHouseName: UntypedFormControl = new UntypedFormControl(null, [
        Validators.required,
    ]);

    /**
     * House number
     *
     * @type {FormControl}
     * @memberof AddressEditorComponent
     */
    addressHouseNumber: UntypedFormControl = new UntypedFormControl(null, [
        Validators.required,
    ]);

    /**
     * Street
     *
     * @type {FormControl}
     * @memberof AddressEditorComponent
     */
    addressStreet: UntypedFormControl = new UntypedFormControl(null, [
        Validators.minLength(3),
        Validators.required,
    ]);

    /**
     * District
     *
     * @type {FormControl}
     * @memberof AddressEditorComponent
     */
    addressDistrict: UntypedFormControl = new UntypedFormControl(null, [
        Validators.minLength(3),
    ]);

    /**
     * Town/city
     *
     * @type {FormControl}
     * @memberof AddressEditorComponent
     */
    addressTownCity: UntypedFormControl = new UntypedFormControl(null, [
        Validators.minLength(3),
        Validators.required,
    ]);

    /**
     * County
     *
     * @type {FormControl}
     * @memberof AddressEditorComponent
     */
    addressCounty: UntypedFormControl = new UntypedFormControl(null, [
        Validators.minLength(3),
    ]);

    /**
     * Postcode
     *
     * @type {FormControl}
     * @memberof AddressEditorComponent
     */
    addressPostcode: UntypedFormControl = new UntypedFormControl(null, [
        Validators.required,
        Validators.pattern(/^([A-Z](([0-9][0-9]?)|([A-Z][0-9][0-9]?)|([A-Z]?[0-9][A-Z])) ?[0-9][ABD-HJLNP-UW-Z]{2})$/i),
    ]);


    /**
     * Provides value to form as to whether user has entered county and if it's valid once entered.
     *
     * @readonly
     * @memberof AddressEditorComponent
     */
    get hasEnteredCounty() {
        const val = this.formAddressEditor.get('addressCounty').value;
        return !_.isNil(val) && val.length > 0;
    }


    get hasEnteredDistrict() {
        const val = this.formAddressEditor.get('addressDistrict').value;
        return !_.isNil(val) && val.length > 0;
    }

    hasEnteredField(field: string) {
        const val = this.formAddressEditor.get(field).value;
        return !_.isNil(val) && val.length > 0;
    }

    switchToSearchMode() {
        this.service.controlMode.next(0);
    }

    addressNumberNameValidator(number: string, name: string) {
        return (group: UntypedFormGroup): { [key: string]: any } => {
            return this.isNameAndNumberInvalid(group) ? {
                addressNumberOrName: true,
            } : null;
        };
    }

    ngAfterContentChecked() {
        this.form_out.emit(this.formAddressEditor);
    }

    /**
     * Initialise component
     *
     * @memberof AddressEditorComponent
     */
    ngOnInit() {
        this.mapIncomingData();
        this.onChanges(false);
    }

    /**
     * Toggles as house number and name are
     *
     * @memberof AddressEditorComponent
     */
    toggleNumberName() {

        // If the house number or name is invalid
        if (this.formAddressEditor.get('addressHouseNumber').valid &&
            this.formAddressEditor.get('addressHouseName').valid) {
            return;
        }

        if (this.formAddressEditor.get('addressHouseNumber').valid &&
            this.formAddressEditor.get('addressHouseName').invalid) {
            this.formAddressEditor.get('addressHouseName').clearValidators();
            this.formAddressEditor.get('addressHouseName').updateValueAndValidity();
        }

        if (this.formAddressEditor.get('addressHouseNumber').invalid &&
            this.formAddressEditor.get('addressHouseName').valid) {
            this.formAddressEditor.get('addressHouseNumber').clearValidators();
            this.formAddressEditor.get('addressHouseNumber').updateValueAndValidity();
        }

        if (this.formAddressEditor.get('addressHouseNumber').invalid &&
            this.formAddressEditor.get('addressHouseName').valid) {
            this.formAddressEditor.get('addressHouseNumber').setValidators([
                Validators.required,
                Validators.minLength(3),
            ]);
            this.formAddressEditor.get('addressHouseName').setValidators([
                Validators.required,
                Validators.minLength(3),
            ]);
        }

    }

    /**
     * Toggle the elements being touched to update the element validation.
     *
     * @param {any} event
     * @memberof AddressEditorComponent
     */
    toggleTouched(event) {
        if (this.formAddressEditor.get('addressHouseNumber').hasError('required') ||
            this.formAddressEditor.get('addressHouseName').hasError('required')) {
            this.toggleNumberName();
        }
    }

    /**
     * Subscribes to address form changes.
     *
     * @param {boolean} [emit_form=true]
     * @memberof AddressEditorComponent
     */
    onChanges(emitForm = true): void {
        this.formAddressEditor.valueChanges.subscribe((val) => {
            this.toggleNumberName();
        });

        this.formAddressEditor.statusChanges.subscribe((val) => {
            if (val === 'VALID') {
                this.mapModelFromForm();
                this.addressIsValid.emit({
                    valid: true,
                    form: this.formAddressEditor,
                });
            } else {
                this.addressIsValid.emit({
                    valid: true,
                    form: this.formAddressEditor,
                });
            }
        });
        if (emitForm === false) {
            this.form_out.emit(this.formAddressEditor);
        }
    }
    private mapIncomingData(): void {
        if (this.service.editMode === 0) {
            this.mapModelFromQas();
        } else {
            this.mapModelFromIncomingAddress();
        }
        this.mapFormFromModel();
    }

    private mapModelFromQas(): void {
        this.service.address = new TogetherBaseAddress();
        this.service.address.Postcode = this.parseQasField(this.service.qasAddress.postcode);
        this.service.address.County = this.parseQasField(this.service.qasAddress.county);
        this.service.address.District = this.parseQasField(this.service.qasAddress.district);
        this.service.address.QASHouseName = this.parseQasField(this.service.qasAddress.buildingName);
        let houseName = '';
        houseName += _.isEmpty(this.service.qasAddress.subBuildingNumber)
            ? '' : `${this.service.qasAddress.subBuildingNumber} `;
        houseName += _.isEmpty(this.service.qasAddress.subBuildingName)
            ? '' : `${this.service.qasAddress.subBuildingName}, `;
        houseName += _.isEmpty(this.service.qasAddress.buildingName)
            ? '' : `${this.service.qasAddress.buildingName} `;

        this.service.address.HouseName = houseName;

        this.service.address.HouseNumber = this.parseQasField(this.service.qasAddress.buildingNumber);
        this.service.address.PostTown = this.parseQasField(this.service.qasAddress.city);
        this.service.address.Street1 = this.parseQasField(this.service.qasAddress.street);
        this.service.address.SubBuildingName = this.parseQasField(
            this.service.qasAddress.subBuildingName,
        );
        this.service.address.SubBuildingNumber = this.parseQasField(
            this.service.qasAddress.subBuildingNumber,
        );

        // Toggle number and name
        this.toggleNumberName();
    }

    private parseQasField(input: any): string {
        if (typeof input === 'string') {
            return input;
        }

        return null;
    }

    /**
     * Maps model from input to service.
     *
     * @private
     * @memberof AddressEditorComponent
     */
    private mapModelFromIncomingAddress(): void {
        this.service.address = this.incomingAddress;
    }


    /**
     * Maps back to model in the case of a QAS address
     * (if it's an incoming DB address it's different.)
     *
     * @private
     * @memberof AddressEditorComponent
     */
    private mapModelFromForm(): void {
        this.service.address.Postcode = this.formAddressEditor.get('addressPostcode').value;
        this.service.address.County = this.formAddressEditor.get('addressCounty').value;
        this.service.address.District = this.formAddressEditor.get('addressDistrict').value;
        this.service.address.HouseName = this.formAddressEditor.get('addressHouseName').value;
        this.service.address.HouseNumber = this.formAddressEditor.get('addressHouseNumber').value;
        this.service.address.PostTown = this.formAddressEditor.get('addressTownCity').value;
        this.service.address.Street2 = this.formAddressEditor.get('addressStreet').value;
        this.service.address.Street1 = this.formAddressEditor.get('addressStreet').value;
        this.service.address['Street'] = this.formAddressEditor.get('addressStreet').value;
    }

    /**
     * Maps form from incoming object.
     *
     * @private
     * @memberof AddressEditorComponent
     */
    private mapFormFromModel(): void {
        if (!_.isNil(this.service.address)) {
            this.formAddressEditor.get('addressDistrict').setValue(this.service.address.District);
            this.formAddressEditor.get('addressHouseName').setValue(this.service.address.HouseName);
            this.formAddressEditor.get('addressHouseNumber').setValue(this.service.address.HouseNumber);
            this.formAddressEditor.get('addressTownCity').setValue(this.service.address.PostTown);
            this.formAddressEditor.get('addressCounty').setValue(this.service.address.County);

            const street = this.service.address['Street'] ||
                this.service.address.Street1 || this.service.address.Street2;

            this.formAddressEditor.get('addressStreet').setValue(street);
            this.formAddressEditor.get('addressPostcode').setValue(this.service.address.Postcode);
        }

        // Toggle number and name
        this.toggleNumberName();
        this.formAddressEditor.updateValueAndValidity();
        this.formAddressEditor.markAsDirty();
    }
    /**
     * Helper to check if a field is invalid by checking .invalid and .touched/.dirty
     *
     * @private
     * @param {string} field
     * @returns {boolean}
     * @memberof AddressEditorComponent
     */
    private isFieldInvalid(field: string): boolean {
        return this.formAddressEditor.get(field).invalid
            && (this.formAddressEditor.get(field).touched || this.formAddressEditor.get(field).dirty
                || this.formAddressEditor.dirty || this.formAddressEditor.touched);
    }
    /**
     * Is postcode Invalid
     *
     * @readonly
     * @type {boolean}
     * @memberof AddressEditorComponent
     */
    get isPostcodeInvalid(): boolean {
        return this.isFieldInvalid('addressPostcode');
    }

    /**
     * Is Street Invalid
     *
     * @readonly
     * @type {boolean}
     * @memberof AddressEditorComponent
     */
    get isStreetInvalid(): boolean {
        return this.isFieldInvalid('addressStreet');
    }
    /**
     * Is TownCity Invalid
     *
     * @readonly
     * @type {boolean}
     * @memberof AddressEditorComponent
     */
    get isTownCityInvalid(): boolean {
        return this.isFieldInvalid('addressTownCity');
    }

    /**
     * Neither Name or Number are valid
     *
     * @readonly
     * @type {boolean}
     * @memberof AddressEditorComponent
     */
    isNameAndNumberInvalid(group: UntypedFormGroup): boolean {
        const theGroup = group || this.formAddressEditor;
        const name = theGroup.get('addressHouseName');
        const number = theGroup.get('addressHouseNumber');
        const nameEmpty = _.isNil(name.value) || (name.value === '');
        const numberEmpty = _.isNil(number.value) || (number.value === '');
        const result = (nameEmpty && numberEmpty)
            && (name.touched || name.dirty || number.touched || number.dirty
                || theGroup.dirty || theGroup.touched);
        return result;
    }
    /**
     * Is form Invalid
     *
     * @readonly
     * @type {boolean}
     * @memberof AddressEditorComponent
     */
    get isFormInvalid(): boolean {
        return this.isPostcodeInvalid
            || this.isStreetInvalid
            || this.isTownCityInvalid
            ;
    }

    /**
     * Creates an instance of AddressEditorComponent.
     * @memberof AddressEditorComponent
     */
    constructor(private service: AddressComponentService, private fb: UntypedFormBuilder) {
        this.service.address = new TogetherBaseAddress();
        this.formAddressEditor = fb.group({
            addressHouseNumber: this.addressHouseNumber,
            addressHouseName: this.addressHouseName,
            addressStreet: this.addressStreet,
            addressDistrict: this.addressDistrict,
            addressTownCity: this.addressTownCity,
            addressCounty: this.addressCounty,
            addressPostcode: this.addressPostcode,
        },
            {
                validator: this.addressNumberNameValidator('addressHouseNumber', 'addressHouseName'),
            });


    }

}
